import React from "react";
import type { ApolloClient } from "@apollo/client";
import { useParams } from "react-router-dom";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { each, get, includes, isEmpty } from "lodash";
import { getApproval, setApprovalStatus } from "actions/adminActions";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { adminConstants } from "actions/types";
import type { RootState } from "types/state";

import ApprovalItem from "pages/admin/ApprovalItem";
import ApprovalDocumentItem from "pages/admin/ApprovalDocumentItem";
import IconSpinner from "components/IconSpinner";

import "./Approval.scss";

// ApprovalItem / ApprovalDocumentItem accept a specific (and, for
// ApprovalDocumentItem, connected) Props shape; this component spreads the
// whole {...this.props} bag (redux + Apollo props it doesn't model field by
// field) into them, so cast to a loose component type so the spread typechecks.
const ApprovalItemTyped = ApprovalItem as React.ComponentType<
  Record<string, unknown>
>;
// ApprovalDocumentItem is a connected component, so a direct cast doesn't
// overlap — route through `unknown` to keep the loose boundary type used when
// spreading {...this.props}.
const ApprovalDocumentItemTyped =
  ApprovalDocumentItem as unknown as React.ComponentType<
    Record<string, unknown>
  >;

const isLoadingSelector = createLoadingSelector(adminConstants.GET_APPROVAL);
const isSubmittingSelector = createLoadingSelector(
  adminConstants.SET_APPROVAL_STATUS
);

const errorSelector = createErrorSelector([
  adminConstants.GET_APPROVAL,
  adminConstants.SET_APPROVAL_STATUS,
]);

interface OwnProps {
  approvalId?: string;
}

const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
  const approvalId = ownProps.approvalId;
  const approval = state.admin.approvalsById[approvalId as string];

  const userId = get(approval, "entityIdentifier.id") as string | undefined;

  const isSelfApproval =
    get(approval, "type.requiresDistinctApprovers") === true &&
    get(approval, "status") === "PENDING" &&
    !!state.user.id &&
    get(approval, "createdByEntity.id") === state.user.id;

  return {
    approval: (approval || {}) as Record<string, unknown>,
    isFetching: isLoadingSelector(state) || !approval,
    isSubmitting: isSubmittingSelector(state),
    approvalId,
    error: errorSelector(state),
    userId,
    isSelfApproval,
  };
};

const mapDispatchToProps = {
  getApproval,
  setApprovalStatus,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  approvalStatus: string;
  comments: string;
  reason: string;
}

/** Form values submitted by ApprovalItem / ApprovalDocumentItem. */
interface ApprovalSubmitValues {
  approvalStatus?: string;
  reason?: string;
  comments?: string;
  metadata?: unknown;
}

class Approval extends React.PureComponent<Props, State> {
  _promises: unknown[] = [];

  constructor() {
    // preserve original no-arg super() call; props are not passed here
    super(undefined as unknown as Props);

    this.state = {
      approvalStatus: "",
      comments: "",
      reason: "NONE",
    };
  }

  componentDidMount() {
    this._promises.push(
      this.props.getApproval(
        this.props.client as ApolloClient<object>,
        // approvalId comes from useParams() as `string | undefined`; getApproval's
        // codegen param is `string | number`. The original JS passed it through
        // unchanged (incl. undefined) — cast preserves that exact runtime value.
        this.props.approvalId as string
      )
    );
  }

  componentWillUnmount() {
    // found-not-fixed: arrow returns promise.cancel but never invokes it, so
    // no promise is actually cancelled (src/pages/admin/Approval.tsx:108)
    each(
      this._promises as Array<{ cancel?: () => void }>,
      (promise) => promise.cancel
    );
  }

  _onSubmit = (values: ApprovalSubmitValues) => {
    const approvalParams = {
      approvalId: this.props.approvalId,
      status: values.approvalStatus,
      reason: values.reason,
      comment: values.comments,
      metadata: values.metadata,
    };
    return this.props.setApprovalStatus(
      this.props.client as ApolloClient<object>,
      // The original JS built this object from optional form/route values
      // (approvalId may be undefined, status from a free-form field) and passed
      // it straight through. Cast to the codegen ApprovalParams at the boundary
      // so those nullable values flow through unchanged — runtime object identical.
      approvalParams as Parameters<typeof setApprovalStatus>[1]
    );
  };

  render() {
    if (this.props.isFetching) {
      return <IconSpinner centered />;
    }

    const approvalTypesWithDetailedView = [
      "EmployerBusinessMemberProfileUpdate",
      "EmployerBusinessRegistrationUpdate",
      "EntityUnfreeze",
      "EntityUnauthorizedAchUnfreeze",
      "UserKycProfileUpdate",
    ];
    const approvalTypeValue = get(this.props.approval, "type.value", "");
    const useDetailedView =
      !isEmpty(this.props.approval.documents) ||
      includes(approvalTypesWithDetailedView, approvalTypeValue);

    if (useDetailedView) {
      return (
        <ApprovalDocumentItemTyped
          {...this.props}
          userId={this.props.userId}
          onSubmit={this._onSubmit}
        />
      );
    }

    return <ApprovalItemTyped {...this.props} onSubmit={this._onSubmit} />;
  }
}

const ConnectedApproval = connector(withApollo<Props>(Approval));

const Approval_WithParams = (props: OwnProps) => {
  const { id: approvalId } = useParams();
  return <ConnectedApproval {...props} approvalId={approvalId} />;
};

export default Approval_WithParams;
